import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { useCallback, useEffect, useMemo } from 'react';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { useSubtotals } from '../../context/SubtotalsProvider';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedList from 'I18n/components/FormattedList';
import { useDispatch, useSelector } from 'react-redux';
import { getShowAllErrors, setErrorMessages } from '../../state/errorMessagesSlice';
import { useGetErrorMessagesAndTranslations } from '../../hooks/useGetErrorMessagesAndTranslations';
import { RECURRING_BILLING_FREQUENCIES } from '../../constants/RecurringBillingFrequencies';
import { getRecurringBillingFrequency } from '../../utils/recurring';
import { jsx as _jsx } from "react/jsx-runtime";
export default function ClickToCancelCheckbox() {
  var _checkoutSessionJS$br;
  const {
    formState,
    dispatch,
    checkoutSession
  } = usePaymentFormContext();
  const reduxDispatch = useDispatch();
  const {
    recurringSubtotals = []
  } = useSubtotals();
  const checkoutSessionJS = checkoutSession.toJS();
  const clickToCancelAcknowledged = formState.get('clickToCancelAcknowledged');
  const loading = formState.get('isCheckoutSubmitPending');
  const recurringSubtotal = recurringSubtotals[0];
  const recurringBillingTerms = recurringSubtotal === null || recurringSubtotal === void 0 ? void 0 : recurringSubtotal.recurringBillingTerms;
  const numPayments = recurringBillingTerms === null || recurringBillingTerms === void 0 ? void 0 : recurringBillingTerms.numPayments;
  const total = recurringSubtotal === null || recurringSubtotal === void 0 ? void 0 : recurringSubtotal.total;
  const IntervalTranslations = useMemo(() => ({
    [RECURRING_BILLING_FREQUENCIES.DAYS]: I18n.text('payments.recurringBillingUnit.days'),
    [RECURRING_BILLING_FREQUENCIES.WEEKS]: I18n.text('payments.recurringBillingUnit.weeks'),
    [RECURRING_BILLING_FREQUENCIES.TWO_WEEKS]: I18n.text('payments.recurringBillingUnit.twoWeeks'),
    [RECURRING_BILLING_FREQUENCIES.MONTHS]: I18n.text('payments.recurringBillingUnit.months'),
    [RECURRING_BILLING_FREQUENCIES.QUARTERS]: I18n.text('payments.recurringBillingUnit.quarters'),
    [RECURRING_BILLING_FREQUENCIES.SIX_MONTHS]: I18n.text('payments.recurringBillingUnit.sixMonths'),
    [RECURRING_BILLING_FREQUENCIES.YEARS]: I18n.text('payments.recurringBillingUnit.years')
  }), []);
  const companyName = (_checkoutSessionJS$br = checkoutSessionJS.branding) === null || _checkoutSessionJS$br === void 0 ? void 0 : _checkoutSessionJS$br.companyName;
  const currencyCode = checkoutSessionJS.currencyCode;
  const showAllErrors = useSelector(getShowAllErrors);
  const isError = showAllErrors && !clickToCancelAcknowledged;
  useEffect(() => {
    reduxDispatch(setErrorMessages({
      clickToCancelNotAcknowledged: {
        isError
      }
    }));
  }, [isError, reduxDispatch]);
  const errorMessage = useGetErrorMessagesAndTranslations().get('clickToCancelNotAcknowledged');
  const handleAcknowledgementChanged = useCallback(event => {
    dispatch({
      type: 'setClickToCancelAcknowledged',
      data: event.target.checked
    });
  }, [dispatch]);
  const recurringLineItems = useMemo(() => (checkoutSessionJS.lineItems || []).filter(item => item.recurringBillingTerms), [checkoutSessionJS.lineItems]);
  const areAllRecurringLineItemsFixedPayments = useMemo(() => recurringLineItems.every(item => {
    var _item$recurringBillin;
    return (_item$recurringBillin = item.recurringBillingTerms) === null || _item$recurringBillin === void 0 ? void 0 : _item$recurringBillin.numPayments;
  }), [recurringLineItems]);
  const hasMultipleLineItemsWithDifferentCadences = useMemo(() => {
    var _recurringLineItems$;
    if (recurringLineItems.length <= 1) {
      return false;
    }
    const firstCadence = (_recurringLineItems$ = recurringLineItems[0]) === null || _recurringLineItems$ === void 0 ? void 0 : _recurringLineItems$.recurringBillingTerms;
    if (!firstCadence) return false;
    return !recurringLineItems.every(item => {
      const terms = item.recurringBillingTerms;
      return terms && terms.intervalUnit === firstCadence.intervalUnit && terms.intervalLength === firstCadence.intervalLength;
    });
  }, [recurringLineItems]);
  const lineItemCadencePaymentsString = useMemo(() => {
    if (!hasMultipleLineItemsWithDifferentCadences) return '';
    const paymentStrings = recurringLineItems.map(item => {
      var _item$recurringBillin2, _item$recurringBillin3;
      const intervalUnit = (_item$recurringBillin2 = item.recurringBillingTerms) === null || _item$recurringBillin2 === void 0 ? void 0 : _item$recurringBillin2.intervalUnit;
      const intervalLength = (_item$recurringBillin3 = item.recurringBillingTerms) === null || _item$recurringBillin3 === void 0 ? void 0 : _item$recurringBillin3.intervalLength;
      const intervalTranslation = intervalUnit && intervalLength ? IntervalTranslations[getRecurringBillingFrequency(intervalUnit, intervalLength)] : '';
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "payments.merchantTermsOfService.clickToCancel.intervalPayment_jsx",
        options: {
          value: item.amount,
          interval: intervalTranslation
        },
        elements: {
          Price: FormattedIntlCurrency
        }
      }, item.id);
    });
    return /*#__PURE__*/_jsx(FormattedList, {
      children: paymentStrings
    });
  }, [recurringLineItems, hasMultipleLineItemsWithDifferentCadences, IntervalTranslations]);
  const checkboxTranslationMessage = useMemo(() => {
    if (
    // Only support different cadences in payment links and quotes
    checkoutSessionJS.sourceApp !== 'INVOICE' && hasMultipleLineItemsWithDifferentCadences) {
      // if not all recurring line items have fixed payments, use auto-renew translation
      return numPayments && areAllRecurringLineItemsFixedPayments ? 'payments.merchantTermsOfService.clickToCancel.checkboxWithDifferentCadencesNumPayments_jsx' : 'payments.merchantTermsOfService.clickToCancel.checkboxWithDifferentCadencesAutoRenew_jsx';
    } else if (numPayments) {
      return 'payments.merchantTermsOfService.clickToCancel.checkboxWithNumPayments_jsx';
    }
    return 'payments.merchantTermsOfService.clickToCancel.checkboxWithAutoRenew_jsx';
  }, [numPayments, checkoutSessionJS.sourceApp, hasMultipleLineItemsWithDifferentCadences, areAllRecurringLineItemsFixedPayments]);
  const intervalUnit = recurringBillingTerms === null || recurringBillingTerms === void 0 ? void 0 : recurringBillingTerms.intervalUnit;
  const intervalLength = recurringBillingTerms === null || recurringBillingTerms === void 0 ? void 0 : recurringBillingTerms.intervalLength;
  const recurringBillingFrequency = intervalUnit && intervalLength ? getRecurringBillingFrequency(intervalUnit, intervalLength) : null;
  return /*#__PURE__*/_jsx(FormControl, {
    required: true,
    error: isError,
    validationMessage: isError && errorMessage,
    children: /*#__PURE__*/_jsx(Checkbox, {
      color: "icon-interactive-default",
      size: "small",
      checked: clickToCancelAcknowledged,
      onChange: handleAcknowledgementChanged,
      disabled: loading,
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: checkboxTranslationMessage,
        options: {
          value: total,
          currencyCode,
          interval: IntervalTranslations[recurringBillingFrequency],
          companyName,
          // numPayments is the total number of payments,
          // need to subtract 1 to get the number of payments after current payment
          numPayments: numPayments ? numPayments - 1 : null,
          lineItemPayments: lineItemCadencePaymentsString
        },
        elements: {
          Price: FormattedIntlCurrency
        }
      })
    })
  });
}